/** Stats summary row showing AVG, median, streak, home/away breakdown */

import { betStreak, hitSummary, teamLineOutcome } from '@parlay-picker/shared/lineOutcome';
import { TEAM_SUMMARY_STAT_DEFS } from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { type ReactElement, useRef, useState } from 'react';
import type { ChartDataPoint } from './types';

interface TeamStatsSummaryRowProps {
  chartData: ChartDataPoint[];
  /** All game data (full history, chronological oldest→newest) for fixed streak */
  allChartData: ChartDataPoint[];
  propLine: number;
  propType: string;
  isUnderBet: boolean;
}

export function TeamStatsSummaryRow({
  chartData,
  allChartData,
  propLine,
  propType,
  isUnderBet,
}: TeamStatsSummaryRowProps): ReactElement | null {
  const [showHelp, setShowHelp] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);

  if (chartData.length === 0) return null;

  const values = chartData.map((g) => g.value);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const sorted = [...values].sort((a, b) => a - b);
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];

  // Home/away record
  const homeGames = chartData.filter((g) => g.isHome);
  const awayGames = chartData.filter((g) => !g.isHome);
  const outcome = (g: ChartDataPoint) => teamLineOutcome(g, propType, propLine, isUnderBet);
  const home = hitSummary(homeGames.map(outcome));
  const away = hitSummary(awayGames.map(outcome));

  // Fixed streak: computed from ALL available games, not just the window; a push is skipped
  const streakRun = betStreak([...allChartData].reverse().map(outcome));

  const valueLabel = propType === 'total' ? 'Total' : 'Margin';

  return (
    <div className="relative">
      <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto py-1 sm:py-1.5 -mx-1 px-1 touch-pan-x">
        {/* AVG */}
        <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
          <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            Avg
          </span>
          <span className="text-[11px] sm:text-xs font-bold text-theme-text-secondary">
            {propType !== 'total' && avg > 0 ? '+' : ''}
            {avg.toFixed(1)}
          </span>
        </div>
        {/* Median */}
        <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
          <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            Med
          </span>
          <span className="text-[11px] sm:text-xs font-bold text-theme-text-secondary">
            {propType !== 'total' && median > 0 ? '+' : ''}
            {median.toFixed(1)}
          </span>
        </div>
        {/* Streak */}
        <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
          <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
            Streak
          </span>
          <span
            className={cn(
              'text-[11px] sm:text-xs font-bold',
              !streakRun
                ? 'text-theme-text-tertiary'
                : streakRun.outcome === 'hit'
                  ? 'text-theme-success'
                  : 'text-theme-danger',
            )}
          >
            {streakRun ? `${streakRun.length} ${streakRun.outcome}` : '0'}
          </span>
        </div>
        {/* Home/Away */}
        {homeGames.length > 0 && (
          <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
            <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
              Home
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-theme-text-secondary">
              {home.hits}/{home.decided}
            </span>
          </div>
        )}
        {awayGames.length > 0 && (
          <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
            <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
              Away
            </span>
            <span className="text-[11px] sm:text-xs font-bold text-theme-text-secondary">
              {away.hits}/{away.decided}
            </span>
          </div>
        )}
        {/* Avg vs Line */}
        {propType !== 'moneyline' && (
          <div className="flex items-center gap-0.5 bg-theme-surface/60 rounded-md px-1 sm:px-1.5 py-0.5 shrink-0">
            <span className="text-[9px] sm:text-[10px] text-theme-text-tertiary uppercase tracking-wide">
              {valueLabel}
            </span>
            <span
              className={cn(
                'text-[11px] sm:text-xs font-bold',
                propType === 'total'
                  ? (isUnderBet ? avg < propLine : avg >= propLine)
                    ? 'text-theme-success'
                    : 'text-theme-danger'
                  : avg > -propLine
                    ? 'text-theme-success'
                    : 'text-theme-danger',
              )}
            >
              {avg > propLine ? '+' : ''}
              {(avg - (propType === 'total' ? propLine : -propLine)).toFixed(1)}
            </span>
          </div>
        )}
        {/* Info icon */}
        <button
          type="button"
          onClick={() => setShowHelp((prev) => !prev)}
          className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-theme-text-tertiary hover:text-theme-text-secondary hover:bg-theme-surface-hover active:bg-theme-surface-hover transition"
          aria-label="Explain stats"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Help tooltip */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            ref={helpRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-theme-text-tertiary bg-theme-surface/40 rounded-md px-2.5 py-2 mt-1">
              {TEAM_SUMMARY_STAT_DEFS.filter(
                (def) => !(def.id === 'statVsLine' && propType === 'moneyline'),
              ).map((def) => (
                <div key={def.id} className={def.id === 'statVsLine' ? 'col-span-2' : undefined}>
                  <span className="font-semibold text-theme-text-secondary">
                    {def.id === 'statVsLine' ? valueLabel : def.label}
                  </span>{' '}
                  — {def.description}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
